import { useToastStore } from '@branch-messenger/willow-ui'
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { use } from 'react'

import {
  getTaxWorkerDetails,
  updateTaxWorkerDeliveryConsent,
  updateTaxWorkerDetails,
} from '@/api/tax'
import { TaxUpdateError } from '@/components/tax/TaxUpdateError'
import { DialogContext } from '@/context'
import { handleHttpException } from '@/lib/httpException'
import {
  getEventPropertiesFromError,
  getEventPropertiesFromHttpError,
  sendAnalyticsEvent,
} from '@/lib/mixpanel'
import { OldIResponse } from '@/types/common'
import { FormDeliveryElection, UpdateTaxWorkerDetails } from '@/types/tax'

export const TAX_WORKER_DETAILS_QUERY = 'tax-worker-details-query'

export const taxWorkerDetailsQueryOptions = queryOptions({
  queryKey: [TAX_WORKER_DETAILS_QUERY],
  queryFn: async () => {
    const { data } = await getTaxWorkerDetails()
    return data
  },
})

export const useUpdateTaxWorkerDetails = () => {
  const { showAlert } = use(DialogContext)
  const addToast = useToastStore(state => state.addToast)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: UpdateTaxWorkerDetails) => {
      const res = await updateTaxWorkerDetails(payload)
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TAX_WORKER_DETAILS_QUERY],
      })
      addToast({
        title: 'Your tax information was successfully updated.',
        variant: 'success',
      })
    },
    onError: error => {
      const title = 'There was an issue updating your tax information.'
      const updateTaxWorkerDetailsEvent = 'update-tax-worker-details:failure'
      handleHttpException<OldIResponse>(error, {
        onHttpError: error => {
          const description = error.response?.data.message ?? ''
          showAlert({
            title: '',
            hideCancel: true,
            children: (
              <TaxUpdateError title={title} description={description} />
            ),
          })
          sendAnalyticsEvent(
            'TAXES',
            updateTaxWorkerDetailsEvent,
            getEventPropertiesFromHttpError(error)
          )
        },
        onOtherError: () => {
          showAlert({
            title,
            description:
              'Please try again or contact your organization for assistance.',
            actionDescription: 'OK',
            hideCancel: true,
          })
          sendAnalyticsEvent(
            'TAXES',
            updateTaxWorkerDetailsEvent,
            getEventPropertiesFromError(error)
          )
        },
      })
    },
  })
}

export const useUpdateTaxWorkerDeliveryConsent = () => {
  const addToast = useToastStore(state => state.addToast)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (formDeliveryElection: FormDeliveryElection) => {
      const res = await updateTaxWorkerDeliveryConsent(formDeliveryElection)
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TAX_WORKER_DETAILS_QUERY],
      })
      addToast({
        title: 'Your preferred 1099 delivery method was successfully updated',
        variant: 'success',
      })
    },
    onError: error => {
      const title =
        'We were unable to update your preferred 1099 delivery method - Please try again.'
      const updateTaxWorkerDeliveryEvent =
        'update-tax-worker-delivery-consent:failure'
      handleHttpException<OldIResponse>(error, {
        onHttpError: error => {
          const description = error.response?.data.message ?? ''
          addToast({
            title,
            description,
            variant: 'alert',
          })
          sendAnalyticsEvent(
            'TAXES',
            updateTaxWorkerDeliveryEvent,
            getEventPropertiesFromHttpError(error)
          )
        },
        onOtherError: () => {
          addToast({
            title,
            description:
              'Please try again or contact your organization for assistance.',
            variant: 'alert',
          })
          sendAnalyticsEvent(
            'TAXES',
            updateTaxWorkerDeliveryEvent,
            getEventPropertiesFromError(error)
          )
        },
      })
    },
  })
}
